<template>
  <div>
    <breadcrumb style="margin-left: -15px">
      <breadcrumb-item>
        <a href="/checklists/new">Checklists</a>
      </breadcrumb-item>
      <breadcrumb-item>
        <span>{{ isEdit ? (checklist.name || 'Edit checklist') : 'New checklist' }}</span>
      </breadcrumb-item>
    </breadcrumb>

    <loading-panel :loading="loading"></loading-panel>

    <card v-if="!loading" body-classes="p-0">
      <div class="cr-run">
        <div class="cr-run__header">
          <div>
            <h3 class="cr-header__title">{{ isEdit ? 'Edit checklist' : 'New checklist' }}</h3>
            <div class="cr-header__meta">
              <span>{{ steps.length }} step{{ steps.length === 1 ? '' : 's' }}</span>
              <span v-if="requiredCount > 0">· {{ requiredCount }} required</span>
              <span v-if="checklist.requires_approval">· Requires approval</span>
            </div>
          </div>
        </div>

        <div class="cr-run__body ce-body">
          <!-- Details column -->
          <div class="ce-details">
            <div class="ce-section__title">Details</div>
            <base-input label="Name" placeholder="Checklist name" v-model="checklist.name" :error="errors.name"></base-input>
            <base-input label="Event (optional)">
              <el-select clearable class="select-primary" style="width: 100%" placeholder="Not linked to an event" v-model="checklist.game_id">
                <el-option v-for="g in games" :key="'G' + g.id" class="select-primary" :label="g.name" :value="g.id"></el-option>
              </el-select>
            </base-input>
            <base-input label="Description">
              <textarea class="form-control" rows="3" placeholder="What this checklist is for" v-model="checklist.description"></textarea>
            </base-input>
            <div class="d-flex align-items-center mt-2">
              <el-switch active-color="#e14eca" v-model="checklist.requires_approval"></el-switch>
              <label class="text-white mb-0 ml-2">Requires approval</label>
              <el-popover trigger="hover" placement="top" width="280">
                <div><div class="popover-body">Completed runs wait for a reviewer with the Approve checklist runs permission, who approves the run or sends it back with a note.</div></div>
                <i slot="reference" class="tim-icons el-icon-info ml-2" style="color: #1d8cf8; cursor: pointer"></i>
              </el-popover>
            </div>
          </div>

          <!-- Steps column -->
          <div class="ce-section">
            <div class="ce-steps-head">
              <div>
                <div class="ce-section__title">Steps</div>
                <small class="ce-steps-head__sub">Ticked in order. Required steps must be done before a run can be completed.</small>
              </div>
              <base-button variant="outlined" tone="primary" class="ml-auto" @click="addStep()">
                <i class="tim-icons icon-simple-add"></i> Add step
              </base-button>
            </div>

            <info-hint v-if="errors.steps" variant="danger" icon="icon-alert-circle-exc">{{ errors.steps }}</info-hint>

            <div v-if="steps.length === 0" class="ce-empty">
              <i class="tim-icons icon-bullet-list-67"></i>
              <div class="ce-empty__text">No steps yet. Use Add step to create the first one.</div>
            </div>

            <draggable v-else v-model="steps" tag="div" class="ce-list" handle=".ce-card__drag">
              <div v-for="(step, i) in steps" :key="step.key" class="ce-card" :class="{ 'ce-card--invalid': invalidKeys.includes(step.key) }" @click="openStep(step)">
                <span class="ce-card__drag" title="Drag to reorder" @click.stop><i class="tim-icons el-icon-rank"></i></span>
                <div class="ce-card__type-icon"><i :class="typeIcon(step.response_type)"></i></div>
                <div class="ce-card__info">
                  <div class="ce-card__label text-white" :class="{ 'ce-card__label--empty': !step.name }">{{ step.name || 'Untitled step' }}</div>
                  <div class="ce-card__meta">
                    <span class="ce-card__type-name">{{ step.response_type === 'checkbox' ? 'Basic' : typeLabel(step.response_type) }}</span>
                    <span v-if="step.response_type === 'dropdown' || step.response_type === 'multiselect'" class="ce-card__meta-detail">{{ step.response_options.length }} option{{ step.response_options.length === 1 ? '' : 's' }}</span>
                    <span v-if="step.img_urls && step.img_urls.length" class="ce-card__meta-detail"><i class="tim-icons icon-image-02 mr-1"></i>{{ step.img_urls.length === 1 ? 'Image' : step.img_urls.length + ' images' }}</span>
                  </div>
                  <div v-if="step.step_description" class="ce-card__desc">{{ htmlToPlainText(step.step_description) }}</div>
                </div>
                <div class="ce-card__actions" @click.stop>
                  <span v-if="!step.is_optional" class="cr-pill">Required</span>
                  <span class="ce-card__num">{{ i + 1 }}</span>
                  <el-dropdown trigger="click">
                    <base-button simple type="primary" link size="sm"><i class="tim-icons el-icon-more"></i></base-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <a class="dropdown-item mb-2" href="#" @click.prevent="openStep(step)"><i class="tim-icons icon-pencil mr-2"></i> Edit step</a>
                        <a class="dropdown-item mb-2" href="#" @click.prevent="duplicateStep(step)"><i class="tim-icons icon-single-copy-04 mr-2"></i> Duplicate</a>
                        <hr>
                        <a class="dropdown-item" href="#" @click.prevent="removeStep(step)"><i class="tim-icons text-danger icon-trash-simple mr-2"></i> Remove step</a>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </div>
              </div>
            </draggable>
          </div>
        </div>

        <div class="ce-footer">
          <base-button variant="outlined" tone="primary" @click="cancel()">Cancel</base-button>
          <base-button type="primary" size="sm" class="cr-cta" :disabled="saving" @click="save()">
            <i class="tim-icons icon-check-2 mr-1"></i> {{ isEdit ? 'Save changes' : 'Create checklist' }}
          </base-button>
        </div>
      </div>
    </card>

    <!-- Step drawer -->
    <base-drawer :show="drawer.show" :title="drawer.isNew ? 'New step' : 'Edit step'" @close="closeDrawer()">
      <drawer-section title="Step basics" icon="icon-app">
        <base-input label="Response type">
          <el-select class="select-primary" style="width: 100%" v-model="draft.response_type">
            <el-option v-for="t in types" :key="t.value" class="select-primary" :label="t.label" :value="t.value"></el-option>
          </el-select>
        </base-input>
        <base-input label="Name" v-model="draft.name" maxlength="500" placeholder="What the runner has to do" :error="drawer.nameError"></base-input>
        <base-input label="Instructions">
          <rich-text-editor v-model="draft.step_description" toolbar="minimal" :allow-images="false" :height="140" :dark="isDarkMode"
                            placeholder="Details, tips, or where to find things. Runners see this on the step." />
        </base-input>
      </drawer-section>

      <drawer-section v-if="draft.response_type === 'dropdown' || draft.response_type === 'multiselect'"
                      :title="draft.response_type === 'dropdown' ? 'Dropdown options' : 'Multi-select options'" icon="icon-bullet-list-67">
        <info-hint icon="icon-bulb-63">
          {{ draft.response_type === 'dropdown' ? 'The runner picks one of these on the step.' : 'The runner can pick several of these on the step.' }}
        </info-hint>
        <info-hint v-if="drawer.optionsError" variant="danger" icon="icon-alert-circle-exc">{{ drawer.optionsError }}</info-hint>
        <div v-for="(o, i) in draft.response_options" :key="'O' + i" class="d-flex align-items-end" style="gap: 6px">
          <div class="flex-grow-1">
            <base-input :label="'Option ' + (i + 1)" :value="o" @input="setOption(i, $event)" placeholder="Option name"></base-input>
          </div>
          <div style="margin-bottom: 12px">
            <el-tooltip content="Remove option" effect="light" :open-delay="300" placement="top">
              <base-button type="danger" icon link size="sm" @click="draft.response_options.splice(i, 1)">
                <i class="tim-icons icon-simple-remove"></i>
              </base-button>
            </el-tooltip>
          </div>
        </div>
        <base-button variant="outlined" tone="primary" @click="draft.response_options.push('')">
          <i class="tim-icons icon-simple-add"></i> Add option
        </base-button>
      </drawer-section>

      <drawer-section title="Behavior" icon="icon-settings-gear-63">
        <div class="d-flex align-items-center">
          <el-switch active-color="#e14eca" v-model="draft.is_optional"></el-switch>
          <label class="text-white mb-0 ml-2">Optional step</label>
          <el-popover trigger="hover" placement="top" width="260">
            <div><div class="popover-body">Optional steps can be left undone. Required steps block completing the run until they are ticked.</div></div>
            <i slot="reference" class="tim-icons el-icon-info ml-2" style="color: #1d8cf8; cursor: pointer"></i>
          </el-popover>
        </div>
      </drawer-section>

      <drawer-section title="Reference images" icon="icon-image-02">
        <info-hint icon="icon-bulb-63">Shown to the runner on the step. Useful for how a room or prop should look when reset. Up to {{ IMAGE_MAX }} images.</info-hint>
        <div class="ce-images">
          <div v-for="(url, i) in draft.img_urls" :key="'RI' + i" class="ce-images__item">
            <el-image :src="url" fit="cover" :preview-src-list="draft.img_urls" :initial-index="i" :z-index="3000" class="ce-image__thumb"></el-image>
            <el-tooltip content="Remove image" effect="light" :open-delay="300" placement="top">
              <button type="button" class="ce-images__remove" :aria-label="'Remove image ' + (i + 1)" @click="removeImage(i)">
                <i class="tim-icons icon-simple-remove"></i>
              </button>
            </el-tooltip>
          </div>
          <label v-if="draft.img_urls.length < IMAGE_MAX" class="ce-images__add">
            <i class="tim-icons" :class="uploading ? 'icon-refresh-02 fa-spin' : 'icon-camera-18'"></i>
            <span>{{ uploading ? 'Uploading' : (draft.img_urls.length === 0 ? 'Add an image' : 'Add') }}</span>
            <input type="file" accept="image/*" :disabled="uploading" @change="onPhoto($event)" style="display: none" />
          </label>
        </div>
      </drawer-section>

      <template #footer>
        <div class="footer-actions">
          <base-button type="secondary" size="sm" @click="closeDrawer()">Cancel</base-button>
          <base-button type="primary" size="sm" @click="saveDrawer()">{{ drawer.isNew ? 'Add step' : 'Save changes' }}</base-button>
        </div>
      </template>
    </base-drawer>

    <base-alert-dialog
      :show="alert.show"
      :title="alert.title"
      :message="alert.message"
      :variant="alert.variant"
      @confirm="onAlertConfirm()"
      @cancel="alert.show = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { isHtml, plainTextToHtml, htmlToPlainText, isEmptyHtml } from '@/util/richText'

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const group = JSON.parse(localStorage.getItem('group') || '{}')
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${group.id}`

const types = [
  { label: 'Basic (done or not done)', value: 'checkbox' },
  { label: 'Dropdown', value: 'dropdown' },
  { label: 'Multi-select', value: 'multiselect' },
  { label: 'Text', value: 'text' },
  { label: 'Number', value: 'number' }
]
const IMAGE_MAX = 10
// Read when the drawer opens; the editor follows the console theme like project tickets do.
const isDarkMode = ref(true)
const uploading = ref(false)
const drawer = reactive({ show: false, isNew: false, key: null as string | null, nameError: '', optionsError: '' })
const draft = reactive<any>({ name: '', step_description: '', img_url: '', img_urls: [] as string[], is_optional: false, response_type: 'checkbox', response_options: [] as string[] })
const TYPE_ICONS: Record<string, string> = {
  checkbox: 'far fa-check-square',
  dropdown: 'tim-icons icon-bullet-list-67',
  multiselect: 'fas fa-tasks',
  text: 'tim-icons icon-pencil',
  number: 'fas fa-hashtag'
}

const checklistId = ref<number | null>(proxy.$route.query.id ? Number(proxy.$route.query.id) : null)
const isEdit = computed(() => checklistId.value !== null)

const loading = ref(true)
const saving = ref(false)
const games = ref<any[]>([])
const checklist = reactive<any>({ name: '', description: '', game_id: null, requires_approval: false })
const steps = ref<any[]>([])
const invalidKeys = ref<string[]>([])
const errors = reactive({ name: '', steps: '' })
const alert = reactive({ show: false, title: '', message: '', variant: 'warning', redirect: '' })

let keySeq = 0
function newKey() { keySeq += 1; return 'k' + keySeq }

const requiredCount = computed(() => steps.value.filter(s => !s.is_optional).length)


async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function parseError(err: any): string {
  const data = err?.response?.data
  if (!data) return 'Something went wrong'
  if (typeof data === 'string') {
    try { return JSON.parse(data).error || data } catch (e) { return data }
  }
  return data.error || 'Something went wrong'
}
function showAlert(title: string, message: string, variant = 'warning', redirect = '') {
  alert.title = title
  alert.message = message
  alert.variant = variant
  alert.redirect = redirect
  alert.show = true
}
function onAlertConfirm() {
  alert.show = false
  if (alert.redirect) proxy.$router.push(alert.redirect)
}
function handleError(err: any) {
  const status = err?.response?.status
  if (status === 401 || status === 403) {
    proxy.$router.push('/forbidden')
    return
  }
  if (status === 500) {
    showAlert('Error', 'The feature has not been enabled as part of your subscription plan.', 'danger', '/')
    return
  }
  if (status === 404) {
    showAlert('Checklist not found', 'This checklist no longer exists.', 'warning', '/checklists/new')
    return
  }
  showAlert('Error', parseError(err), 'danger')
}

function typeLabel(t: string) { return (types.find(x => x.value === t) || types[0]).label }
function typeIcon(t: string) { return TYPE_ICONS[t] || TYPE_ICONS.checkbox }

function loadDraft(from: any) {
  draft.name = from.name || ''
  draft.step_description = isHtml(from.step_description) ? (from.step_description || '') : plainTextToHtml(from.step_description || '')
  draft.img_urls = Array.isArray(from.img_urls) ? [...from.img_urls] : (from.img_url ? [from.img_url] : [])
  draft.img_url = draft.img_urls[0] || ''
  draft.is_optional = !!from.is_optional
  draft.response_type = from.response_type || 'checkbox'
  draft.response_options = Array.isArray(from.response_options) ? [...from.response_options] : []
  drawer.nameError = ''
  drawer.optionsError = ''
}
function openStep(step: any) {
  isDarkMode.value = !document.body.classList.contains('white-content')
  drawer.isNew = false
  drawer.key = step.key
  loadDraft(step)
  drawer.show = true
}
function closeDrawer() { drawer.show = false }
function setOption(i: number, value: string) { draft.response_options.splice(i, 1, value) }
function saveDrawer() {
  drawer.nameError = ''
  drawer.optionsError = ''
  const cleanedOptions = draft.response_options.map((o: string) => o.trim()).filter((o: string) => o !== '')
  const needsOptions = draft.response_type === 'dropdown' || draft.response_type === 'multiselect'
  if (!draft.name.trim()) drawer.nameError = 'Give the step a name'
  if (needsOptions && cleanedOptions.length === 0) drawer.optionsError = 'Add at least one option.'
  if (drawer.nameError || drawer.optionsError) return
  const values = {
    name: draft.name.trim(),
    step_description: isEmptyHtml(draft.step_description) ? '' : draft.step_description,
    img_urls: [...draft.img_urls],
    img_url: draft.img_urls[0] || '',
    is_optional: draft.is_optional,
    response_type: draft.response_type,
    response_options: needsOptions ? cleanedOptions : []
  }
  if (drawer.isNew) {
    steps.value.push({ ...normalizeStep({}), ...values })
  } else {
    const target = steps.value.find(s => s.key === drawer.key)
    if (target) Object.assign(target, values)
  }
  invalidKeys.value = invalidKeys.value.filter(k => k !== drawer.key)
  drawer.show = false
}
function duplicateStep(step: any) {
  const idx = steps.value.findIndex(s => s.key === step.key)
  const copy = { ...normalizeStep(step), id: null, response_options: [...step.response_options], img_urls: [...(step.img_urls || [])] }
  steps.value.splice(idx + 1, 0, copy)
}

async function onPhoto(event: any) {
  const file = event.target.files && event.target.files[0]
  if (!file) return
  uploading.value = true
  try {
    const config = await authConfig()
    const signed = await axios.post(`${API_LOCATION}upload`, { filePath: file.name, contentType: file.type }, config)
    const uploadUrl: string = signed.data
    await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } })
    if (draft.img_urls.length < IMAGE_MAX) draft.img_urls.push(uploadUrl.split('?')[0])
  } catch (err) {
    showAlert('Upload failed', 'The image could not be uploaded. Try again.', 'danger')
  } finally {
    uploading.value = false
    event.target.value = ''
  }
}

function removeImage(i: number) { draft.img_urls.splice(i, 1) }

function normalizeStep(raw: any) {
  let options = raw.response_options
  if (typeof options === 'string') { try { options = JSON.parse(options) } catch (e) { options = [] } }
  let imgs = raw.img_urls
  if (typeof imgs === 'string') { try { imgs = JSON.parse(imgs) } catch (e) { imgs = [] } }
  const imgUrls: string[] = Array.isArray(imgs) && imgs.length ? imgs : (raw.img_url ? [raw.img_url] : [])
  return {
    key: newKey(),
    id: raw.id ?? null,
    name: raw.name || '',
    step_description: raw.step_description || '',
    img_url: imgUrls[0] || '',
    img_urls: imgUrls,
    is_optional: raw.is_optional === 1 || raw.is_optional === true,
    response_type: raw.response_type || 'checkbox',
    response_options: Array.isArray(options) ? options : []
  }
}

function addStep() {
  isDarkMode.value = !document.body.classList.contains('white-content')
  drawer.isNew = true
  drawer.key = null
  loadDraft({})
  drawer.show = true
}
function removeStep(step: any) {
  const idx = steps.value.findIndex(s => s.key === step.key)
  if (idx >= 0) steps.value.splice(idx, 1)
}

async function load() {
  loading.value = true
  try {
    const config = await authConfig()
    const gamesRes = await axios.get(`${baseUrl}/games`, config)
    games.value = gamesRes.data || []
    if (isEdit.value) {
      const res = await axios.get(`${baseUrl}/checklists/${checklistId.value}`, config)
      checklist.name = res.data.checklist?.name || ''
      checklist.description = res.data.checklist?.description || ''
      checklist.game_id = res.data.checklist?.game_id || null
      checklist.requires_approval = res.data.checklist?.requires_approval === 1
      steps.value = (res.data.steps || []).map(normalizeStep)
    }
  } catch (err) {
    handleError(err)
  } finally {
    loading.value = false
  }
}

function validate(): boolean {
  errors.name = ''
  errors.steps = ''
  invalidKeys.value = []
  let ok = true
  if (!checklist.name.trim()) {
    errors.name = 'Give the checklist a name'
    ok = false
  }
  const bad: string[] = []
  for (const s of steps.value) {
    if (!s.name.trim()) bad.push(s.key)
    else if ((s.response_type === 'dropdown' || s.response_type === 'multiselect') && s.response_options.length === 0) bad.push(s.key)
  }
  if (steps.value.length === 0) {
    errors.steps = 'Add at least one step.'
    ok = false
  } else if (bad.length) {
    invalidKeys.value = bad
    errors.steps = bad.length === 1 ? 'One step needs a name, or options for its dropdown.' : bad.length + ' steps need a name, or options for their dropdown.'
    ok = false
  }
  return ok
}

async function save() {
  if (!validate()) return
  saving.value = true
  try {
    const config = await authConfig()
    const payload = {
      checklist: { name: checklist.name.trim(), description: checklist.description || '', game_id: checklist.game_id || null, requires_approval: checklist.requires_approval ? 1 : 0 },
      steps: steps.value.map((s, i) => ({
        id: s.id,
        name: s.name.trim(),
        step_description: s.step_description || '',
        img_url: (s.img_urls && s.img_urls[0]) || '',
        img_urls: s.img_urls || [],
        is_optional: s.is_optional ? 1 : 0,
        response_type: s.response_type,
        response_options: s.response_options,
        position: i
      }))
    }
    if (isEdit.value) {
      await axios.put(`${baseUrl}/checklists/${checklistId.value}`, payload, config)
      proxy.$notify({ type: 'success', message: 'Checklist saved', icon: 'tim-icons icon-check-2' })
      // Reload so new steps pick up their ids and a second save updates instead of inserting again.
      await load()
    } else {
      const res = await axios.post(`${baseUrl}/checklists`, payload, config)
      proxy.$notify({ type: 'success', message: 'Checklist created', icon: 'tim-icons icon-check-2' })
      const newId = res.data && res.data.id
      if (newId) {
        checklistId.value = Number(newId)
        proxy.$router.replace({ path: '/checklists/edit', query: { id: String(newId) } })
        await load()
      } else {
        proxy.$router.push('/checklists/new')
      }
    }
  } catch (err) {
    handleError(err)
  } finally {
    saving.value = false
  }
}

function cancel() { proxy.$router.push('/checklists/new') }

onMounted(() => { load() })
</script>

<script lang="ts">
import draggable from 'vuedraggable'
import { Select, Option, Switch, Dropdown, DropdownMenu } from 'element-ui'
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue'
import BreadcrumbItem from '@/components/Breadcrumb/BreadcrumbItem.vue'
import RichTextEditor from '@/components/RichTextEditor.vue'

export default {
  name: 'checklist-editor',
  components: {
    draggable,
    [Select.name]: Select,
    [Option.name]: Option,
    [Switch.name]: Switch,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    Breadcrumb,
    BreadcrumbItem,
    RichTextEditor
  }
}
</script>
